import { AnimationEvent, usePopup } from '@libs/hooks';
import { TBottomSheetProps } from '@libs/types';
import { Portal } from '@libs/utils';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useEffectEvent, useState } from 'react';
import { flushSync } from 'react-dom';
import { zIndex } from '../utils/constants';

const Overlay = styled('div')({
    alignItems: 'center',
    background: 'rgba(0, 0, 0, 0.48)',
    bottom: 0,
    display: 'block',
    justifyContent: 'center',
    left: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: zIndex.overlay,
});

const PopupOuter = styled('div')({
    alignItems: 'flex-end',
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    padding: '0 16px',
    '@media screen and (min-width: 708px)': {
        alignItems: 'center',
    },
});

const PopupBox = styled('div')<{ $animated?: boolean }>((props) => ({
    '@keyframes getOutAnimation': {
        '0%': {
            transform: 'translateY(0)',
        },
        '100%': {
            transform: 'translateY(90vh)',
        },
    },
    animationName: props.$animated ? 'getOutAnimation' : '',
    animationDuration: '0.6s',
    animationDelay: '0.1s',
    animationTimingFunction: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    animationIterationCount: 1,
    overflow: 'hidden',
    bottom: '24px',
    maxWidth: '568px',
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',

    '@media screen and (min-width: 708px)': {
        bottom: 'auto',
        borderRadius: '16px',
        maxHeight: 'calc(100vh - 96px)',
    },
}));

const PopupBoxContentWrapper = styled('div')(({ theme }) => [
    {
        background: theme.palette.common.white,
        borderRadius: '12px',
        maxHeight: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',

        '@media screen and (min-width: 708px)': {
            padding: 0,
            borderRadius: 16,
        },
    },

    theme.applyStyles('dark', {
        background: theme.palette.common.black,
    }),
]);

const PopupBoxContent = styled('div')({
    maxHeight: '100%',
    overflowY: 'auto',
});

export const BottomSheet = ({ widgetId, modal, setBottomSheet, bottomSheets, WidgetsList }: TBottomSheetProps) => {
    const [animated, setAnimated] = useState(false);
    const [id, setId] = useState<string>('');
    const currentBottomSheet = bottomSheets?.[id];
    const { handlers, overlayRef, overlayClick } = usePopup(id && currentBottomSheet, setAnimated, modal);

    const updatePopup = useEffectEvent((i: string, wId?: string) => {
        if (wId) {
            setId(wId);
        } else if (i) {
            setAnimated(true);
        }
    });

    useEffect(() => {
        updatePopup(id, widgetId);
    }, [id, widgetId]);

    const closePopupAndClearState = useCallback(() => {
        setId('');
        setAnimated(false);
        setBottomSheet(undefined);
    }, [setBottomSheet]);

    const closePopup = useEffectEvent(() => {
        closePopupAndClearState();
    });

    useEffect(() => {
        if (!currentBottomSheet) {
            closePopup();
        }
    }, [currentBottomSheet]);

    const closeAnimationPopup = useCallback(
        (e: AnimationEvent<HTMLDivElement>) => {
            if (e?.animationName === 'getOutAnimation') {
                flushSync(() => {
                    closePopupAndClearState();
                });
            }
        },
        [closePopupAndClearState],
    );

    if (id && currentBottomSheet) {
        return (
            <Portal>
                <div id="bottom-sheet" {...handlers}>
                    <Overlay onClick={overlayClick}>
                        <PopupOuter>
                            <PopupBox $animated={animated} onAnimationEnd={closeAnimationPopup} ref={overlayRef}>
                                <PopupBoxContentWrapper>
                                    <PopupBoxContent>
                                        <WidgetsList uids={currentBottomSheet.uid} />
                                    </PopupBoxContent>
                                </PopupBoxContentWrapper>
                            </PopupBox>
                        </PopupOuter>
                    </Overlay>
                </div>
            </Portal>
        );
    }
    return null;
};
